import React, { useState, useEffect, useMemo } from 'react';
import { IcPlus, IcSearch, IcClose, IcFlask, IcPencil, IcTrash, IcSave } from '../../theme/icons';
import FournisseursLabosSwitch from '../../components/FournisseursLabosSwitch';
import Swal from '../../theme/swal';
import laboratoireService from '../../services/laboratoireService';
import KI from '../../theme/palette';
import { filterSearch } from '../../utils/search';


const inputClass = 'inp w-full rounded-xl px-3.5 py-2.5 text-base text-gray-700 transition';
const labelClass = 'block text-sm font-medium text-gray-600 mb-1.5';

export default function MobileLaboratoireList() {
  const [laboratoires, setLaboratoires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Add / edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // laboratoire being edited, or null for create
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLaboratoires = async () => {
    try {
      setLoading(true);
      const data = await laboratoireService.getAllLaboratoires();
      setLaboratoires(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching laboratoires:', err);
      setError('Impossible de charger les laboratoires.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLaboratoires();
  }, []);

  // Règle Ki Para (utils/search.js) : début de mot (nom, description), sans
  // accents, « commence par » en tête.
  const displayed = useMemo(
    () => filterSearch(laboratoires, searchTerm, { text: (l) => [l.name, l.description] }),
    [laboratoires, searchTerm],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '' });
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (lab) => {
    setEditing(lab);
    setForm({ name: lab.name || '', description: lab.description || '' });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormError('Le nom du laboratoire est requis.');
      return;
    }
    try {
      setSaving(true);
      setFormError(null);
      const payload = { name: form.name.trim(), description: form.description.trim() };
      if (editing) {
        await laboratoireService.updateLaboratoire(editing.id, payload);
      } else {
        await laboratoireService.createLaboratoire(payload);
      }
      setShowModal(false);
      await fetchLaboratoires();
    } catch (err) {
      console.error('Error saving laboratoire:', err);
      setFormError(err.message || "Une erreur est survenue lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (lab) => {
    Swal.fire({
      title: 'Supprimer ce laboratoire ?',
      text: lab.name,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      cancelButtonColor: KI.peacock,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        setDeletingId(lab.id);
        await laboratoireService.deleteLaboratoire(lab.id);
        await fetchLaboratoires();
      } catch (err) {
        console.error('Error deleting laboratoire:', err);
        Swal.fire(
          'Erreur',
          'Échec de la suppression du laboratoire. Il est peut-être lié à des produits.',
          'error'
        );
      } finally {
        setDeletingId(null);
      }
    });
  };

  return (
    <div className="w-full px-4 py-6">
      {/* La bascule segmentée sert de titre (pleine largeur). */}
      <div className="mb-4">
        <FournisseursLabosSwitch full />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher un laboratoire..."
          className="w-full pl-11 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <IcClose />
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : displayed.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcFlask className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">
            {searchTerm
              ? 'Aucun laboratoire correspondant à la recherche'
              : 'Aucun laboratoire enregistré'}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {displayed.map((lab) => (
            <div
              key={lab.id}
              className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-200"
            >
              <div className="h-14 w-14 rounded-xl bg-gray-100 text-gray-500 flex-shrink-0 flex items-center justify-center font-semibold">
                {(lab.name || '?').trim().charAt(0).toUpperCase()}
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{lab.name}</div>
                <div className="text-xs text-gray-500 truncate">
                  {lab.description ? lab.description : 'Sans description'}
                </div>
              </div>

              <div className="flex flex-col gap-2 flex-shrink-0">
                <button
                  onClick={() => openEdit(lab)}
                  aria-label="Modifier"
                  className="h-9 w-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                >
                  <IcPencil />
                </button>
                <button
                  onClick={() => handleDelete(lab)}
                  disabled={deletingId === lab.id}
                  aria-label="Supprimer"
                  className={`h-9 w-9 flex items-center justify-center rounded-xl text-red-600 hover:bg-red-50 transition-colors ${
                    deletingId === lab.id ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <IcTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Floating create button */}
      <button
        onClick={openCreate}
        aria-label="Ajouter un laboratoire"
        className="btn-p fixed right-4 bottom-24 z-30 h-14 w-14 rounded-full shadow-lg transition-colors"
      >
        <IcPlus className="text-xl" />
      </button>

      {/* Add / Edit modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Entête de modale = aplat encre, titre blanc + ✕ (Composants §10). */}
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">
                {editing ? 'Modifier le laboratoire' : 'Nouveau laboratoire'}
              </h3>
              <button onClick={closeModal} aria-label="Fermer" className="text-gray-400 hover:text-white text-xl">
                <IcClose />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                    {formError}
                  </div>
                )}
                <div>
                  <label className={labelClass} htmlFor="name">
                    Nom <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    className={inputClass}
                    placeholder="Nom du laboratoire"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="description">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={form.description}
                    onChange={handleFormChange}
                    className={`${inputClass} min-h-[100px]`}
                    placeholder="Description (optionnel)"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-g px-5 py-2.5 rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-p flex items-center py-2.5 px-6 rounded-xl transition-colors"
                >
                  {saving ? (
                    <>
                      <span className="mr-2 inline-block h-4 w-4 rounded-full border-2 border-t-transparent border-white animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <IcSave className="mr-2" /> {editing ? 'Mettre à jour' : 'Enregistrer'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
